import { cloneDeep, isEqual } from 'lodash';
import { trackConfig, type IFramework } from '@/framework';
import {
    LayerType,
    MaterialIdsType,
    type ILayerProps,
    type IVectorLayerProps,
    type VectorTextInfo,
} from '@/version-types';
import { pushHistory, resolveVectorTextLayout, resolveVectorTextLayoutAsync, updateLayer } from '../utils';

export interface IFontBase {
    /**
     * 设置字体
     * @param layerID - 图层 ID
     * @param fontFamily - 字体名称
     * @param materialID - 字体素材 ID
     * @returns
     */
    setFontFamily(layerID: string, fontFamily: string, materialID: string): Promise<ILayerProps | void>;

    /**
     * 设置字号
     * @param layerID - 图层 ID
     * @param fontSize - 字号
     * @returns
     */
    setFontSize(layerID: string, fontSize: number, options?: { writeHistory?: boolean }): ILayerProps | void;

    /**
     * 设置字体 italic (font-style)
     * @param layerID - 图层 ID
     * @param flag - 是否设置 italic
     * @returns
     */
    setItalicStyle(layerID: string, flag: boolean): ILayerProps | void;

    /**
     * 设置字体 bold (font-weight)
     * @param layerID - 图层 ID
     * @param flag - 是否设置 bold
     * @returns
     */
    setBoldStyle(layerID: string, flag: boolean): ILayerProps | void;
}

export class FontBase implements IFontBase {
    private prevLayer: ILayerProps | null = null;

    private readonly fontFamilyRequestVersions = new Map<string, number>();

    constructor(private framework: IFramework) {}

    private updateMaterialIDs(layer: ILayerProps, materialID: string) {
        const prevMaterialIDMap = layer.materialIds || {};
        const nextFontFamilyMaterialIDs = [materialID];

        const nextMaterialIDMap = cloneDeep(prevMaterialIDMap);
        nextMaterialIDMap[MaterialIdsType.FONT_FAMILY] = nextFontFamilyMaterialIDs;

        return nextMaterialIDMap;
    }

    /**
     * 等待目标字体进入终态后原子提交字体、素材 ID 与最终几何。
     *
     * @param layerID - 目标 Vector Layer ID。
     * @param fontFamily - 新字体族。
     * @param materialID - 新字体素材 ID。
     * @returns 最新调用成功提交的 Vector；输入失效或被更新输入取代时返回 void 或当前层。
     */
    async setFontFamily(layerID: string, fontFamily: string, materialID: string) {
        const prevLayer = this.framework.service.flatLayer.getLayer(layerID, true);
        if (!prevLayer || prevLayer.layerType !== LayerType.VECTOR_LAYER) return;

        const layerData = prevLayer.data as IVectorLayerProps;
        const { textInfo } = layerData;

        if (!textInfo) return;

        const nextTextInfo: VectorTextInfo = { ...textInfo, fontFamily };
        const nextLayerData: IVectorLayerProps = { ...layerData, textInfo: nextTextInfo };
        let nextLayer: ILayerProps = { ...prevLayer, data: nextLayerData };
        nextLayer.materialIds = this.updateMaterialIDs(nextLayer, materialID);
        const requestVersion = (this.fontFamilyRequestVersions.get(layerID) ?? 0) + 1;
        this.fontFamilyRequestVersions.set(layerID, requestVersion);
        nextLayer = await resolveVectorTextLayoutAsync(this.framework, nextLayer);

        // 同层字体请求可能逆序完成；只有最新 API 请求可以继续竞争当前 owner。
        const latestLayer = this.framework.service.flatLayer.getLayer(layerID, true);
        if (this.fontFamilyRequestVersions.get(layerID) !== requestVersion) return latestLayer;
        this.fontFamilyRequestVersions.delete(layerID);

        // layoutTextAsync 期间也可能有其它 API 成为当前 owner；旧候选不得覆盖其 textInfo 或几何。
        if (!isEqual(latestLayer, prevLayer)) return latestLayer;

        updateLayer(this.framework, nextLayer);
        pushHistory(this.framework, prevLayer, nextLayer, trackConfig[LayerType.TEXT_LAYER].family);

        return nextLayer;
    }

    /**
     * 更新字号并通过宿主 Renderer 同步提交布局。
     *
     * @param layerID - 目标 Vector Layer ID。
     * @param fontSize - 新字号。
     * @param options - 是否结束连续调节并写入历史。
     * @returns 更新后的 Vector；输入不适用时返回 void。
     */
    setFontSize(layerID: string, fontSize: number, options = { writeHistory: true }) {
        const prevLayer = (() => {
            if (this.prevLayer) return this.prevLayer;
            this.prevLayer = this.framework.service.flatLayer.getLayer(layerID, true);
            return this.prevLayer;
        })();

        if (!prevLayer || prevLayer.layerType !== LayerType.VECTOR_LAYER) return;

        const layerData = prevLayer.data as IVectorLayerProps;
        const { textInfo } = layerData;

        if (!textInfo) return;

        const nextTextInfo: VectorTextInfo = { ...textInfo, fontSize };
        const nextLayerData: IVectorLayerProps = { ...layerData, textInfo: nextTextInfo };
        const nextLayer = resolveVectorTextLayout(this.framework, { ...prevLayer, data: nextLayerData });

        const { writeHistory } = options;

        updateLayer(this.framework, nextLayer);
        if (writeHistory) {
            pushHistory(this.framework, prevLayer, nextLayer, trackConfig[LayerType.TEXT_LAYER].size);
            this.prevLayer = null;
        }

        return nextLayer;
    }

    /**
     * 更新整段粗体或斜体并通过唯一 layout commit 写回。
     *
     * @param layerID - 目标 Vector Layer ID。
     * @param kind - 待更新的字体样式字段。
     * @param flag - 是否启用样式。
     * @returns 更新后的 Vector；输入不适用时返回 void。
     */
    private setFontStyle(layerID: string, kind: 'italic' | 'bold', flag: boolean) {
        const prevLayer = this.framework.service.flatLayer.getLayer(layerID, true);
        if (!prevLayer || prevLayer.layerType !== LayerType.VECTOR_LAYER) return;

        const layerData = prevLayer.data as IVectorLayerProps;
        const { textInfo } = layerData;

        if (!textInfo) return;

        const nextTextInfo: VectorTextInfo = { ...textInfo, [kind]: flag };
        const nextLayerData: IVectorLayerProps = { ...layerData, textInfo: nextTextInfo };
        const nextLayer = resolveVectorTextLayout(this.framework, { ...prevLayer, data: nextLayerData });

        updateLayer(this.framework, nextLayer);
        pushHistory(this.framework, prevLayer, nextLayer, trackConfig[LayerType.TEXT_LAYER][kind]);

        return nextLayer;
    }

    setItalicStyle(layerID: string, flag: boolean) {
        return this.setFontStyle(layerID, 'italic', flag);
    }

    setBoldStyle(layerID: string, flag: boolean) {
        return this.setFontStyle(layerID, 'bold', flag);
    }
}
